import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getDealerUsers, resendDealerUserInvite, disableDealerUser, enableDealerUser, sendDealerUserPasswordReset,
} from '../../services/dealerUserService'
import { apiError } from '../../api/apiError'
import type { DealerUser } from '../../types'
import DealerUserModal from './DealerUserModal'
import DealerUserPasswordModal from './DealerUserPasswordModal'
import './DealerUsers.css'

interface Props {
  branchId: string
  readOnly: boolean
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function DealerUsersCard({ branchId, readOnly, onToast }: Props) {
  const { t } = useTranslation()

  const [users, setUsers] = useState<DealerUser[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [passwordUser, setPasswordUser] = useState<DealerUser | null>(null)

  useEffect(() => {
    getDealerUsers(branchId)
      .then(setUsers)
      .catch((err) => {
        console.error('[ERROR] Failed to load dealer users', err)
        onToast(t('admin.dealer_users.load_failed'), 'error')
      })
      .finally(() => setLoading(false))
    // перезагружаем только при смене филиала: onToast пересоздаётся родителем на каждом рендере
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId])

  function replaceUser(updated: DealerUser) {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
  }

  async function runAction(user: DealerUser, fn: () => Promise<DealerUser>, successMsg: string, failMsg: string) {
    setBusyId(user.id)
    try {
      replaceUser(await fn())
      onToast(successMsg, 'success')
    } catch (err) {
      const { code, message } = apiError(err)
      const errorMap: Record<string, string> = {
        EMAIL_TAKEN: t('admin.dealer_users.error_email_taken'),
        USER_ALREADY_ACTIVE: t('admin.dealer_users.error_already_active'),
        BRANCH_ARCHIVED: t('admin.dealer_users.error_branch_archived'),
        USER_NOT_ACTIVATED: t('admin.dealer_users.error_not_activated'),
        USER_DISABLED: t('admin.dealer_users.error_user_disabled'),
        EMAIL_SEND_FAILED: t('admin.dealer_users.error_email_send_failed'),
      }
      onToast((code && errorMap[code]) || message || failMsg, 'error')
    } finally {
      setBusyId(null)
    }
  }

  function handleSendReset(user: DealerUser) {
    if (!confirm(t('admin.dealer_users.reset_confirm', { email: user.email }))) return
    runAction(user, () => sendDealerUserPasswordReset(branchId, user.id), t('admin.dealer_users.reset_sent', { email: user.email }), t('admin.dealer_users.action_failed'))
  }

  function handleDisable(user: DealerUser) {
    if (!confirm(t('admin.dealer_users.disable_confirm', { email: user.email }))) return
    runAction(user, () => disableDealerUser(branchId, user.id), t('admin.dealer_users.disabled'), t('admin.dealer_users.action_failed'))
  }

  return (
    <div className="admin-detail-card dealer-users">
      <div className="dealer-users__header">
        <p className="admin-detail-card__title">{t('admin.dealer_users.card_title')}</p>
        {!readOnly && !loading && (
          <button className="admin-btn admin-btn--primary admin-btn--xs" onClick={() => setShowModal(true)}>
            {t('admin.dealer_users.add')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="admin-spinner" />
      ) : users.length === 0 ? (
        <p className="dealer-users__empty">{t('admin.dealer_users.empty')}</p>
      ) : (
        <div className="dealer-users__list">
          <div className="dealer-users__row dealer-users__row--head">
            <span>{t('admin.dealer_users.col_user')}</span>
            <span>{t('admin.dealer_users.col_status')}</span>
            <span>{t('admin.dealer_users.col_created')}</span>
            <span>{t('admin.dealer_users.col_last_login')}</span>
            <span />
          </div>
          {users.map((u) => {
            const busy = busyId === u.id
            return (
              <div key={u.id} className="dealer-users__row">
                <span className="dealer-users__user">
                  <span className="admin-table__primary">{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</span>
                  <span className="admin-table__secondary">{u.email}</span>
                </span>
                <span><DealerUserStatusBadge status={u.status} /></span>
                <span className="admin-table__secondary">{formatDate(u.createdAt)}</span>
                <span className="admin-table__secondary">{u.lastLoginAt ? formatDate(u.lastLoginAt) : t('admin.dealer_users.never')}</span>
                <span className="dealer-users__actions">
                  {!readOnly && u.status === 'INVITED' && (
                    <button
                      className="admin-btn admin-btn--secondary admin-btn--xs"
                      disabled={busy}
                      onClick={() => runAction(u, () => resendDealerUserInvite(branchId, u.id), t('admin.dealer_users.invite_resent', { email: u.email }), t('admin.dealer_users.action_failed'))}
                    >
                      {t('admin.dealer_users.resend_invite')}
                    </button>
                  )}
                  {!readOnly && u.status !== 'DISABLED' && (
                    <button className="admin-btn admin-btn--secondary admin-btn--xs" disabled={busy} onClick={() => setPasswordUser(u)}>
                      {t('admin.dealer_users.set_password')}
                    </button>
                  )}
                  {!readOnly && u.status === 'ACTIVE' && (
                    <button className="admin-btn admin-btn--secondary admin-btn--xs" disabled={busy} onClick={() => handleSendReset(u)}>
                      {t('admin.dealer_users.reset_password')}
                    </button>
                  )}
                  {!readOnly && u.status !== 'DISABLED' && (
                    <button className="admin-btn admin-btn--danger-outline admin-btn--xs" disabled={busy} onClick={() => handleDisable(u)}>
                      {t('admin.dealer_users.disable')}
                    </button>
                  )}
                  {!readOnly && u.status === 'DISABLED' && (
                    <button
                      className="admin-btn admin-btn--secondary admin-btn--xs"
                      disabled={busy}
                      onClick={() => runAction(u, () => enableDealerUser(branchId, u.id), t('admin.dealer_users.enabled'), t('admin.dealer_users.action_failed'))}
                    >
                      {t('admin.dealer_users.enable')}
                    </button>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {passwordUser && (
        <DealerUserPasswordModal
          branchId={branchId}
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
          onSaved={(updated) => {
            replaceUser(updated)
            setPasswordUser(null)
            onToast(t('admin.dealer_users.password_set', { email: updated.email }), 'success')
          }}
        />
      )}

      {showModal && (
        <DealerUserModal
          branchId={branchId}
          onClose={() => setShowModal(false)}
          onSaved={(created) => {
            setUsers((prev) => [...prev, created])
            setShowModal(false)
            onToast(t('admin.dealer_users.created', { email: created.email }), 'success')
          }}
        />
      )}
    </div>
  )
}

function DealerUserStatusBadge({ status }: { status: DealerUser['status'] }) {
  const { t } = useTranslation()
  return (
    <span className={`dealer-users__status dealer-users__status--${status.toLowerCase()}`}>
      {t(`admin.dealer_users.status_${status.toLowerCase()}`)}
    </span>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
